import { createContext, useLayoutEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { FeatureFlag } from '@revelio/core';

import {
  useUnleashFlag,
  useUnleashFlags,
} from '../../../shared/unleash-flags/use-unleash-flag';
import {
  PageType,
  type ReportPageConfig,
} from '../../CustomDash/components/PageConfigProvider/types';
import { usePageManager } from '../../CustomDash/components/PageManagerProvider/PageManagerProvider';
import { withReportDocumentPages } from '../../CustomDash/report-document/report-document-converters';
import { useReportDocumentsQuery } from '../../CustomDash/report-document/use-report-documents';
import { getLocalReportTemplate } from '../../CustomDash/templates/reports/report-templates-registry';
import { getLocalTemplates } from '../../CustomDash/utils/page-config-lookup';
import {
  globalAssistantReportLaunchPendingStore,
  readPromotedLaunchSeq,
  useGlobalAssistantReportLaunchPendingStore,
} from '../../global-ai-assistant/global-ai-assistant-report-launch-pending';
import { getPromotedTemplateIds } from '../utils/report-template-promotions';
import { useAllReports } from './report-builder.repository';

/**
 * This context is used to manage the custom report builder flow while other reports uses Redux for state management.
 */
export const CustomReportBuilderContext = createContext<{
  customReportsIsEnabled: boolean;
  experimentalReportsIsEnabled: boolean;
  customReportTemplates: ReportPageConfig[];
  experimentalReportTemplates: ReportPageConfig[];
  isLoadingReportTemplates: boolean;
  selectedReportTemplate: ReportPageConfig | null;
  setReportTemplate: (reportTemplate: ReportPageConfig | null) => void;
}>({
  customReportsIsEnabled: false,
  experimentalReportsIsEnabled: false,
  customReportTemplates: [],
  experimentalReportTemplates: [],
  isLoadingReportTemplates: false,
  selectedReportTemplate: null,
  setReportTemplate: () => {
    throw new Error('setReportTemplate not implemented on default context');
  },
});

/**
 * This context provider is used to manage the custom report builder flow while other reports uses Redux for state management.
 */
export const CustomReportBuilderProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const customReportsIsEnabled = useUnleashFlag(FeatureFlag.CustomReports);
  const experimentalReportsIsEnabled = useUnleashFlag(
    FeatureFlag.ExperimentalReports
  );
  const allFlags = useUnleashFlags();
  const promotedTemplateIds = getPromotedTemplateIds(
    allFlags,
    customReportsIsEnabled
  );
  const { pages, loading: isLoadingPages } = usePageManager();
  const { data: reportDocuments, isLoading: isLoadingReportDocuments } =
    useReportDocumentsQuery();
  const isLoadingReportTemplates = isLoadingPages || isLoadingReportDocuments;
  const localTemplates = getLocalTemplates();
  const localReportTemplates = localTemplates.filter(
    (page) => page.type === PageType.REPORT
  ) as ReportPageConfig[];
  const remoteReportPages = withReportDocumentPages(
    pages,
    reportDocuments ?? []
  ).filter((page): page is ReportPageConfig => page.type === PageType.REPORT);
  const remoteReportIds = new Set(remoteReportPages.map((p) => p.id));
  const customReportTemplates = remoteReportPages;
  const experimentalReportTemplates = localReportTemplates.filter(
    (t) => !remoteReportIds.has(t.id) && !promotedTemplateIds.has(t.id)
  );
  const pendingPromotedTemplateId = useGlobalAssistantReportLaunchPendingStore(
    (state) => state.promotedTemplateId
  );
  const pendingLaunchSeq = useGlobalAssistantReportLaunchPendingStore(
    (state) => state.launchSeq
  );
  const visitLaunchSeq = readPromotedLaunchSeq(useLocation().state);
  const [launchSeqAtMount] = useState(
    () => globalAssistantReportLaunchPendingStore.getState().launchSeq
  );
  // A launch already pending when the builder mounted counts only if this
  // visit is the navigation that delivered it; otherwise it never got here.
  const pendingLaunchIsCurrent =
    pendingPromotedTemplateId !== null &&
    (pendingLaunchSeq !== launchSeqAtMount ||
      visitLaunchSeq === pendingLaunchSeq);
  const mwReportCount = useAllReports().length;
  const pendingReportTemplate =
    pendingLaunchIsCurrent && pendingPromotedTemplateId
      ? (getLocalReportTemplate(pendingPromotedTemplateId) ?? null)
      : null;
  const [selectedReportTemplate, setSelectedReportTemplate] =
    useState<ReportPageConfig | null>(pendingReportTemplate);
  const activeReportTemplate = pendingReportTemplate ?? selectedReportTemplate;

  useLayoutEffect(() => {
    if (pendingPromotedTemplateId) {
      const { paramSuggestions, actions } =
        globalAssistantReportLaunchPendingStore.getState();
      if (!pendingLaunchIsCurrent) {
        actions.clearPromotedLaunch();
        return;
      }
      const template = getLocalReportTemplate(pendingPromotedTemplateId);
      if (template) {
        setSelectedReportTemplate(template);
      }
      // The builder holds the template now, so a launch with nothing to
      // prefill is done; the prefill hook releases one with suggestions.
      if (paramSuggestions.length === 0) {
        actions.clearPromotedLaunch();
      }
      return;
    }

    if (mwReportCount > 0) {
      setSelectedReportTemplate(null);
    }
  }, [pendingPromotedTemplateId, pendingLaunchIsCurrent, mwReportCount]);

  const selectReportTemplate = (reportTemplate: ReportPageConfig | null) => {
    setSelectedReportTemplate(reportTemplate);
  };

  return (
    <CustomReportBuilderContext.Provider
      value={{
        customReportsIsEnabled,
        experimentalReportsIsEnabled,
        customReportTemplates,
        experimentalReportTemplates,
        isLoadingReportTemplates,
        selectedReportTemplate: activeReportTemplate,
        setReportTemplate: selectReportTemplate,
      }}
    >
      {children}
    </CustomReportBuilderContext.Provider>
  );
};
